import {
  type InfiniteData,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { type PaginatedChatHistoryResponse } from '@/types/agents';

interface UsePaginatedChatHistoryOptions {
  pageSize?: number;
  enabled?: boolean;
  maxPagesInWindow?: number;
  scope?: string;
}

export interface PaginatedChatHistoryState {
  items: PaginatedChatHistoryResponse['data']['items'];
  isLoading: boolean;
  isLoadingMore: boolean;
  isLoadingNewer: boolean;
  hasMore: boolean;
  hasNewer: boolean;
  error: string | null;
  loadMore: () => Promise<void>;
  loadNewer: () => Promise<void>;
  reset: (options?: { invalidate?: boolean }) => void;
}

export const usePaginatedChatHistory = (
  fetchFunction: (
    page: number,
    pageSize: number
  ) => Promise<PaginatedChatHistoryResponse>,
  activeAgent: string | null,
  options: UsePaginatedChatHistoryOptions = {}
): PaginatedChatHistoryState => {
  const {
    pageSize = 50,
    enabled = true,
    maxPagesInWindow = 3,
    scope = 'dashboard',
  } = options;
  const queryClient = useQueryClient();
  const shouldQuery = enabled && Boolean(activeAgent);

  const queryKey = useMemo(
    () => ['chatHistory', scope, 'paginated', activeAgent, pageSize] as const,
    [scope, activeAgent, pageSize]
  );

  const query = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 1 }) => {
      const response = await fetchFunction(pageParam, pageSize);

      if (!response?.status) {
        const retryableErrors = new Set([
          'AXIOS_NOT_READY',
          'NO_AUTH_HEADER',
          'AGENT_SWITCH_TIMEOUT',
        ]);
        if (response?.error && retryableErrors.has(response.error)) {
          throw new Error(response.error);
        }
      }

      return response;
    },
    enabled: shouldQuery,
    staleTime: 10 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 3,
    retryDelay: attempt => Math.min(1000 * 2 ** attempt, 8000),
    getNextPageParam: lastPage => {
      if (!lastPage?.status || !lastPage?.data) return undefined;
      const totalPages = Math.ceil(lastPage.data.total / pageSize);
      return lastPage.data.page < totalPages
        ? lastPage.data.page + 1
        : undefined;
    },
    getPreviousPageParam: firstPage => {
      if (!firstPage?.status || !firstPage?.data) return undefined;
      return firstPage.data.page > 1 ? firstPage.data.page - 1 : undefined;
    },
  });

  const {
    data,
    isLoading: isQueryLoading,
    isFetchingNextPage,
    isFetchingPreviousPage,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
  } = query;

  const trimWindow = useCallback(
    (direction: 'older' | 'newer') => {
      queryClient.setQueryData<InfiniteData<PaginatedChatHistoryResponse>>(
        queryKey,
        previous => {
          if (!previous || previous.pages.length <= maxPagesInWindow) {
            return previous;
          }

          if (direction === 'older') {
            return {
              ...previous,
              pages: previous.pages.slice(-maxPagesInWindow),
              pageParams: previous.pageParams.slice(-maxPagesInWindow),
            };
          }

          return {
            ...previous,
            pages: previous.pages.slice(0, maxPagesInWindow),
            pageParams: previous.pageParams.slice(0, maxPagesInWindow),
          };
        }
      );
    },
    [maxPagesInWindow, queryClient, queryKey]
  );

  const items = useMemo(() => {
    const pages = data?.pages ?? [];
    return pages.flatMap(page => page?.data?.items ?? []);
  }, [data?.pages]);

  const error = useMemo(() => {
    const failedPage = data?.pages.find(page => page?.status === false);
    return failedPage?.error || null;
  }, [data?.pages]);

  const loadMore = useCallback(async () => {
    if (!shouldQuery) return;
    if (!hasNextPage || isFetchingNextPage) return;
    await fetchNextPage();
    trimWindow('older');
  }, [shouldQuery, hasNextPage, isFetchingNextPage, fetchNextPage, trimWindow]);

  const loadNewer = useCallback(async () => {
    if (!shouldQuery) return;
    if (!hasPreviousPage || isFetchingPreviousPage) return;
    await fetchPreviousPage();
    trimWindow('newer');
  }, [
    shouldQuery,
    hasPreviousPage,
    isFetchingPreviousPage,
    fetchPreviousPage,
    trimWindow,
  ]);

  const hasMore = useMemo(() => {
    if (!shouldQuery) return false;
    const pages = data?.pages ?? [];
    const latestKnownPage = pages.at(-1)?.data?.page;
    const total = pages.at(-1)?.data?.total ?? pages[0]?.data?.total;
    if (!latestKnownPage || !total) return Boolean(hasNextPage);
    const totalPages = Math.ceil(total / pageSize);
    return latestKnownPage < totalPages;
  }, [shouldQuery, pageSize, data?.pages, hasNextPage]);

  const hasNewer = useMemo(() => {
    if (!shouldQuery) return false;
    const pages = data?.pages ?? [];
    const earliestKnownPage = pages[0]?.data?.page;
    if (!earliestKnownPage) return Boolean(hasPreviousPage);
    return earliestKnownPage > 1;
  }, [shouldQuery, data?.pages, hasPreviousPage]);

  const reset = useCallback(
    (options?: { invalidate?: boolean }) => {
      queryClient.removeQueries({ queryKey });
      if (options?.invalidate) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
    [queryClient, queryKey]
  );

  return {
    items,
    isLoading: shouldQuery ? isQueryLoading : false,
    isLoadingMore: shouldQuery ? isFetchingNextPage : false,
    isLoadingNewer: shouldQuery ? isFetchingPreviousPage : false,
    hasMore,
    hasNewer,
    error,
    loadMore,
    loadNewer,
    reset,
  };
};
